import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Form, Button, Container, Alert, Card, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import './pages.css';

const CompleteProfile = () => {
  const { user, api, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const googleUserData = location.state?.userData;
  const [formData, setFormData] = useState({
    phone: user?.phone || googleUserData?.phone || '',
    address: user?.address || googleUserData?.address || ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  // Verify session on component mount
  useEffect(() => {
    const verifySession = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        logout();
        navigate('/login', {
          state: { 
            from: location.pathname,
            message: 'Please login to complete your profile'
          }
        });
        return;
      }

      try {
        await api.get('/auth/verify-token');
        setIsVerifying(false);

        // If user already has phone and address, redirect to dashboard
        if (user?.phone && user?.address) {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        logout();
        navigate('/login', {
          state: { 
            from: location.pathname,
            message: 'Session expired. Please login again.'
          }
        });
      }
    };

    verifySession();
  }, [api, logout, navigate, location, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Session expired - please login again');
      }

      // Update the user profile
      const response = await api.put('/auth/update', formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Update user data in storage with the complete response
      localStorage.setItem('user', JSON.stringify(response.data));
      setUser(response.data);

      navigate('/dashboard');
    } catch (err) {
      console.error('Profile update failed:', err);
      
      if (err.response?.status === 401 || err.message.includes('expired')) {
        logout();
        navigate('/login', {
          state: {
            from: location.pathname,
            message: 'Session expired. Please login again.'
          }
        });
      } else {
        setError(err.response?.data?.message || err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Verifying session...</span>
      </div>
    );
  }

  return (
    <div className="complete-profile-page">
      <Container className="complete-profile-container">
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="py-4">
            <Card className="profile-card shadow-sm">
              <Card.Body>
                <div className="text-center mb-4">
                  <h2 className="profile-title">Complete Your Profile</h2>
                  <p className="profile-subtitle">Please provide the following information to complete your profile</p>
                </div>
                
                {error && <Alert variant="danger" className="text-center">{error}</Alert>}
                
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="form-input"
                      placeholder="Enter your phone number"
                    />
                  </Form.Group>

                  <div className="text-center">
                    <Button 
                      variant="primary" 
                      type="submit" 
                      disabled={isLoading}
                      className="submit-button"
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Saving...
                        </>
                      ) : 'Save Profile'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CompleteProfile;